import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";

const CATEGORIES = [
  { id: "todas", label: "Todas" },
  { id: "no-pican", label: "Los que no pican" },
  { id: "machas", label: "Las Machas" },
  { id: "habaneros", label: "Los Habaneros" },
  { id: "variedad", label: "La Variedad" },
  { id: "kit", label: "Kits" },
];

const SPICES = [
  { id: "todos", label: "Todo picor" },
  { id: "suave", label: "Suave" },
  { id: "medio", label: "Medio" },
  { id: "picante", label: "Picante" },
];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("todas");
  const [spice, setSpice] = useState("todos");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (category !== "todas") list = list.filter((p) => p.category === category);
    if (spice === "suave") list = list.filter((p) => p.spice_level <= 2);
    if (spice === "medio") list = list.filter((p) => p.spice_level === 3);
    if (spice === "picante") list = list.filter((p) => p.spice_level >= 4);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(s) || p.short.toLowerCase().includes(s) || p.tags?.some((t) => t.includes(s))
      );
    }
    return list;
  }, [products, category, spice, q]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8" data-testid="catalog-page">
      <p className="font-script font-semibold text-2xl text-orange">Elige tu favorita</p>
      <h1 className="mt-2 font-script text-4xl font-semibold text-cocoa sm:text-5xl">La tienda</h1>
      <p className="mt-4 max-w-lg text-clay">
        Tandas pequeñas, ingredientes locales, higiene y mucho amor en cada frasco. Envío a domicilio o pick up en Mérida.
      </p>

      <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por categoría">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-category-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`rounded-full border px-5 py-2 text-sm font-bold transition-colors ${
                category === c.id
                  ? "border-habanero bg-habanero text-white"
                  : "border-sand bg-white text-cocoa hover:border-habanero hover:text-habanero"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por nivel de picor">
            {SPICES.map((s) => (
              <button
                key={s.id}
                data-testid={`filter-spice-${s.id}`}
                onClick={() => setSpice(s.id)}
                className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                  spice === s.id
                    ? "border-habanero bg-habanero text-white"
                    : "border-sand bg-white text-clay hover:border-habanero hover:text-habanero"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-clay" />
            <Input
              data-testid="catalog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar salsa..."
              className="w-48 rounded-full border-sand bg-white pl-9"
              aria-label="Buscar producto"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <p className="py-24 text-center font-script font-semibold text-3xl text-clay">Calentando el comal...</p>
      ) : filtered.length === 0 ? (
        <p className="py-24 text-center font-script font-semibold text-3xl text-clay" data-testid="catalog-empty">
          No encontramos salsas con esos filtros. ¡Prueba otra combinación!
        </p>
      ) : (
        <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div key={p.slug} layout exit={{ opacity: 0, scale: 0.9 }}>
                <ProductCard product={p} index={i} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
